import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  useTheme,
} from "@mui/material";
import { NoticeboardButton } from "../shared";
import { composerOutlinedFieldSx } from "./composerBorderStyles";
import { findNoticePlaceholders } from "../../utils/noticePlaceholders";
import {
  htmlToPlainText,
  isPlainNoticeBody,
} from "../../utils/noticeBodyHtml";
import { useFeatureFlags } from "../../hooks/useFeatureFlags";
import RichBodyEditor from "./RichBodyEditor";

interface PlaceholderEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: boolean;
  helperText?: string;
  multiline?: boolean;
  minRows?: number;
  maxRows?: number;
  inputProps?: any;
  sx?: any;
  /**
   * When true, hides the underlying input while there are placeholders
   * and only shows the clickable placeholder helper. Once all placeholders
   * are replaced, the input becomes visible again.
   */
  hideInputWhenHasPlaceholders?: boolean;
  /**
   * When true, hides the placeholder helper section below the input.
   */
  hidePlaceholderHelper?: boolean;
  /**
   * Use TipTap rich body editor (B/I/U + lists). Defaults to true for multiline
   * when the noticeBodyFormatting feature flag is on.
   */
  richText?: boolean;
}

interface PlaceholderMatch {
  fullMatch: string;
  variableName: string;
  startIndex: number;
  endIndex: number;
}

const PlaceholderEditor: React.FC<PlaceholderEditorProps> = ({
  value,
  onChange,
  placeholder,
  error,
  helperText,
  multiline = false,
  minRows,
  maxRows,
  inputProps,
  sx,
  hideInputWhenHasPlaceholders = false,
  hidePlaceholderHelper = false,
  richText,
}) => {
  const theme = useTheme();
  const { isNoticeBodyFormattingEnabled } = useFeatureFlags();
  const [editingPlaceholder, setEditingPlaceholder] =
    useState<PlaceholderMatch | null>(null);
  const [replacementValue, setReplacementValue] = useState("");
  const useRichBody =
    isNoticeBodyFormattingEnabled && (richText ?? multiline);

  const placeholders = useMemo(() => {
    const scanSource = isPlainNoticeBody(value || "")
      ? value || ""
      : htmlToPlainText(value || "");
    return findNoticePlaceholders(scanSource);
  }, [value]);

  const handlePlaceholderClick = useCallback(
    (placeholderMatch: PlaceholderMatch) => {
      setEditingPlaceholder(placeholderMatch);
      setReplacementValue("");
    },
    [],
  );

  const handleReplacePlaceholder = useCallback(() => {
    if (!editingPlaceholder) return;

    const idx = value.indexOf(editingPlaceholder.fullMatch);
    if (idx === -1) {
      setEditingPlaceholder(null);
      setReplacementValue("");
      return;
    }
    const newValue =
      value.substring(0, idx) +
      replacementValue +
      value.substring(idx + editingPlaceholder.fullMatch.length);

    onChange(newValue);
    setEditingPlaceholder(null);
    setReplacementValue("");
  }, [editingPlaceholder, replacementValue, value, onChange]);

  const handleCloseDialog = useCallback(() => {
    setEditingPlaceholder(null);
    setReplacementValue("");
  }, []);

  const renderTextWithPlaceholders = () => {
    const display = isPlainNoticeBody(value || "")
      ? value || ""
      : htmlToPlainText(value || "");
    if (placeholders.length === 0) {
      return <span>{display || placeholder}</span>;
    }

    const parts: React.ReactNode[] = [];
    let lastIndex = 0;

    placeholders.forEach((placeholderMatch, index) => {
      if (placeholderMatch.startIndex > lastIndex) {
        const textBefore = display.substring(
          lastIndex,
          placeholderMatch.startIndex,
        );
        if (textBefore) {
          parts.push(<span key={`text-${index}`}>{textBefore}</span>);
        }
      }

      parts.push(
        <Box
          key={`placeholder-${index}`}
          component="span"
          onClick={(e) => {
            e.stopPropagation();
            handlePlaceholderClick(placeholderMatch);
          }}
          sx={{
            color: theme.palette.primary.main,
            textDecoration: "underline",
            cursor: "pointer",
            fontWeight: 600,
            backgroundColor: theme.palette.primary.main + "15",
            padding: "2px 4px",
            borderRadius: "4px",
            display: "inline-block",
            margin: "0 2px",
            "&:hover": {
              backgroundColor: theme.palette.primary.main + "25",
            },
          }}
        >
          {placeholderMatch.fullMatch}
        </Box>,
      );

      lastIndex = placeholderMatch.endIndex;
    });

    if (lastIndex < display.length) {
      const textAfter = display.substring(lastIndex);
      if (textAfter) {
        parts.push(<span key="text-end">{textAfter}</span>);
      }
    }

    return parts;
  };

  const showRichEditor =
    useRichBody &&
    !(hideInputWhenHasPlaceholders && placeholders.length > 0);

  return (
    <>
      <Box sx={{ position: "relative", width: "100%" }}>
        {showRichEditor ? (
          <RichBodyEditor
            value={value}
            onChange={onChange}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            minRows={minRows}
          />
        ) : (
          <TextField
            fullWidth
            variant="outlined"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            error={error}
            helperText={helperText}
            multiline={multiline}
            minRows={minRows}
            maxRows={maxRows}
            inputProps={inputProps}
            sx={[
              composerOutlinedFieldSx,
              {
                display:
                  hideInputWhenHasPlaceholders && placeholders.length > 0
                    ? "none"
                    : "block",
              },
              ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
            ]}
          />
        )}
        {placeholders.length > 0 && !hidePlaceholderHelper && (
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              backgroundColor: theme.palette.grey[50],
              borderRadius: 1,
              border: `1px solid ${theme.palette.grey[200]}`,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                display: "block",
                mb: 1,
                color: "text.secondary",
                fontWeight: 500,
              }}
            >
              Click a value to fill it in:
            </Typography>
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: 0.5,
                fontSize: (t) => t.typography.body2.fontSize,
                lineHeight: 1.6,
              }}
            >
              {renderTextWithPlaceholders()}
            </Box>
          </Box>
        )}
      </Box>

      <Dialog
        open={!!editingPlaceholder}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Fill in the value</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: "text.secondary" }}>
            What goes in <strong>{editingPlaceholder?.fullMatch}</strong>?
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            value={replacementValue}
            onChange={(e) => setReplacementValue(e.target.value)}
            placeholder="Type what goes here..."
            autoFocus
            multiline
            minRows={7}
            maxRows={8}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.ctrlKey) {
                e.preventDefault();
                handleReplacePlaceholder();
              }
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <NoticeboardButton
            variant="primary"
            onClick={handleReplacePlaceholder}
            disabled={!replacementValue.trim()}
          >
            Fill in
          </NoticeboardButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PlaceholderEditor;
